import React, { useEffect, useRef } from "react";
import Logo from "../assets/logo.png";

const clamp = (value, min, max) =>
  Math.min(max, Math.max(min, value));

const buildEdgePoints = (image, size) => {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d", {
    willReadFrequently: true,
  });
  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(image, 0, 0, size, size);
  const { data } = ctx.getImageData(0, 0, size, size);
  const edgePoints = [];
  const threshold = 50;
  const step = 2;
  const index = (x, y) => (y * size + x) * 4 + 3;
  for (let y = 1; y < size - 1; y += step) {
    for (let x = 1; x < size - 1; x += step) {
      const alpha = data[index(x, y)];
      if (alpha <= threshold) continue;
      const neighbors = [
        data[index(x - 1, y)],
        data[index(x + 1, y)],
        data[index(x, y - 1)],
        data[index(x, y + 1)],
      ];
      if (neighbors.some((value) => value <= threshold)) {
        edgePoints.push({ x, y });
      }
    }
  }
  return edgePoints;
};

export default function FireLogo({
  size = 56,
  className = "",
  alt = "BetterBMRCalculator logo",
  ...rest
}) {
  const flameRef = useRef(null);
  const smokeRef = useRef(null);
  const emberRef = useRef(null);

  useEffect(() => {
    const flameCanvas = flameRef.current;
    const smokeCanvas = smokeRef.current;
    const emberCanvas = emberRef.current;
    if (!flameCanvas || !smokeCanvas || !emberCanvas) return undefined;

    const dpr = window.devicePixelRatio || 1;
    const flameWidth = size * 1.2;
    const flameHeight = size * 1.4;
    const originX = (flameWidth - size) / 2;
    const originY = flameHeight - size;

    const setupCanvas = (canvas) => {
      canvas.width = flameWidth * dpr;
      canvas.height = flameHeight * dpr;
      canvas.style.width = `${flameWidth}px`;
      canvas.style.height = `${flameHeight}px`;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return ctx;
    };

    const flameCtx = setupCanvas(flameCanvas);
    const smokeCtx = setupCanvas(smokeCanvas);
    const emberCtx = setupCanvas(emberCanvas);

    let edgePoints = [];
    let baseEdgePoints = [];
    const flames = [];
    const smoke = [];
    const embers = [];
    let lastTime = performance.now();
    let flameSpawn = 0;
    let smokeSpawn = 0;
    let emberSpawn = 0;
    const maxFlames = Math.max(10, Math.round(size * 0.25));
    let running = true;

    const logoImage = new Image();
    logoImage.src = Logo;
    logoImage.onload = () => {
      edgePoints = buildEdgePoints(logoImage, size);
      baseEdgePoints = edgePoints.filter(
        (point) => point.y > size * 0.55
      );
      requestAnimationFrame(animate);
    };

    const spawnFlame = () => {
      const pool =
        baseEdgePoints.length > 0 && Math.random() < 0.7
          ? baseEdgePoints
          : edgePoints;
      if (!pool.length) return;
      const point = pool[Math.floor(Math.random() * pool.length)];
      const jitter = () => (Math.random() - 0.5) * 3;
      const baseFactor = point.y / size;
      if (flames.length >= maxFlames) return;
      flames.push({
        x: originX + point.x + jitter(),
        y: originY + point.y + jitter(),
        vx: (Math.random() - 0.5) * 0.4,
        vy: 0.5 + Math.random() * 0.9,
        life: 12 + Math.random() * 18,
        maxLife: 12 + Math.random() * 18,
        radius: 2 + Math.random() * 4,
        baseFactor,
      });
    };

    const spawnSmoke = () => {
      const origin = flames[Math.floor(Math.random() * flames.length)];
      if (!origin) return;
      smoke.push({
        x: origin.x + (Math.random() - 0.5) * 6,
        y: origin.y - 8,
        vx: (Math.random() - 0.5) * 0.2,
        vy: 0.2 + Math.random() * 0.25,
        life: 40 + Math.random() * 30,
        maxLife: 40 + Math.random() * 30,
        radius: 6 + Math.random() * 8,
      });
    };

    const spawnEmber = () => {
      const origin = flames[Math.floor(Math.random() * flames.length)];
      if (!origin) return;
      embers.push({
        x: origin.x + (Math.random() - 0.5) * 8,
        y: origin.y,
        vx: (Math.random() - 0.5) * 0.6,
        vy: 1.1 + Math.random() * 0.8,
        life: 16 + Math.random() * 10,
        maxLife: 16 + Math.random() * 10,
        radius: 0.8 + Math.random() * 1.2,
      });
    };

    const animate = (time) => {
      if (!running) return;
      const delta = Math.min(32, time - lastTime) / 16.67;
      lastTime = time;
      const wind =
        Math.sin(time / 1000) * 0.2 +
        Math.cos(time / 1700) * 0.1;

      flameSpawn += delta * (size / 90);
      smokeSpawn += delta * (size / 120);
      emberSpawn += delta * (size / 70);

      while (flameSpawn >= 1) {
        spawnFlame();
        flameSpawn -= 1;
      }
      while (smokeSpawn >= 1) {
        spawnSmoke();
        smokeSpawn -= 1;
      }
      while (emberSpawn >= 1) {
        spawnEmber();
        emberSpawn -= 1;
      }

      flameCtx.clearRect(0, 0, flameWidth, flameHeight);
      smokeCtx.clearRect(0, 0, flameWidth, flameHeight);
      emberCtx.clearRect(0, 0, flameWidth, flameHeight);

      flameCtx.globalCompositeOperation = "screen";
      flameCtx.globalAlpha = 0.6;
      flames.forEach((flame) => {
        flame.life -= delta;
        flame.y -= flame.vy * delta;
        flame.x += (flame.vx + wind) * delta;
        flame.vx += (Math.random() - 0.5) * 0.05;

        const lifeRatio = clamp(flame.life / flame.maxLife, 0, 1);
        const radius =
          flame.radius * (0.6 + (1 - lifeRatio) * 0.6);

        const gradient = flameCtx.createRadialGradient(
          flame.x,
          flame.y,
          0,
          flame.x,
          flame.y,
          radius
        );
        gradient.addColorStop(
          0,
          `rgba(255,255,255,${0.22 * lifeRatio})`
        );
        gradient.addColorStop(
          0.2,
          `rgba(255,220,150,${0.3 * lifeRatio})`
        );
        gradient.addColorStop(
          0.45,
          `rgba(255,150,60,${0.26 * lifeRatio})`
        );
        gradient.addColorStop(
          0.75,
          `rgba(200,50,0,${0.16 * lifeRatio})`
        );
        gradient.addColorStop(1, "rgba(40,0,0,0)");
        flameCtx.fillStyle = gradient;
        flameCtx.beginPath();
        flameCtx.arc(flame.x, flame.y, radius, 0, Math.PI * 2);
        flameCtx.fill();

        if (flame.baseFactor > 0.6 && lifeRatio > 0.6) {
          const blueRadius = radius * 0.35;
          const blueGradient = flameCtx.createRadialGradient(
            flame.x,
            flame.y + blueRadius * 0.2,
            0,
            flame.x,
            flame.y + blueRadius * 0.2,
            blueRadius
          );
          blueGradient.addColorStop(
            0,
            `rgba(120,200,255,${0.08 * lifeRatio})`
          );
          blueGradient.addColorStop(
            1,
            "rgba(120,200,255,0)"
          );
          flameCtx.fillStyle = blueGradient;
          flameCtx.beginPath();
          flameCtx.arc(
            flame.x,
            flame.y + blueRadius * 0.2,
            blueRadius,
            0,
            Math.PI * 2
          );
          flameCtx.fill();
        }
      });
      flameCtx.globalAlpha = 1;

      for (let i = flames.length - 1; i >= 0; i -= 1) {
        if (flames[i].life <= 0 || flames[i].y < -40) {
          flames.splice(i, 1);
        }
      }

      smokeCtx.globalCompositeOperation = "source-over";
      smokeCtx.globalAlpha = 0.2;
      smokeCtx.filter = "blur(4px)";
      smoke.forEach((particle) => {
        particle.life -= delta;
        particle.y -= particle.vy * delta;
        particle.x += (particle.vx + wind * 0.2) * delta;
        const lifeRatio = clamp(
          particle.life / particle.maxLife,
          0,
          1
        );
        const radius =
          particle.radius * (0.7 + (1 - lifeRatio) * 0.6);
        const smokeGradient = smokeCtx.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          radius
        );
        smokeGradient.addColorStop(
          0,
          `rgba(80,70,60,${0.1 * lifeRatio})`
        );
        smokeGradient.addColorStop(
          1,
          "rgba(40,30,25,0)"
        );
        smokeCtx.fillStyle = smokeGradient;
        smokeCtx.beginPath();
        smokeCtx.arc(
          particle.x,
          particle.y,
          radius,
          0,
          Math.PI * 2
        );
        smokeCtx.fill();
      });
      smokeCtx.filter = "none";
      smokeCtx.globalAlpha = 1;
      for (let i = smoke.length - 1; i >= 0; i -= 1) {
        if (smoke[i].life <= 0 || smoke[i].y < -60) {
          smoke.splice(i, 1);
        }
      }

      emberCtx.globalCompositeOperation = "lighter";
      embers.forEach((ember) => {
        ember.life -= delta;
        ember.y -= ember.vy * delta;
        ember.x += ember.vx * delta;
        const lifeRatio = clamp(ember.life / ember.maxLife, 0, 1);
        emberCtx.fillStyle = `rgba(255,160,60,${
          0.6 * lifeRatio
        })`;
        emberCtx.beginPath();
        emberCtx.arc(
          ember.x,
          ember.y,
          ember.radius,
          0,
          Math.PI * 2
        );
        emberCtx.fill();
      });
      for (let i = embers.length - 1; i >= 0; i -= 1) {
        if (embers[i].life <= 0 || embers[i].y < -50) {
          embers.splice(i, 1);
        }
      }

      requestAnimationFrame(animate);
    };

    return () => {
      running = false;
    };
  }, [size]);

  return (
    <div
      className={`fire-logo ${className}`}
      style={{ width: size, height: size }}
      {...rest}
    >
      <canvas
        ref={smokeRef}
        className="fire-logo__smoke"
        aria-hidden="true"
      />
      <canvas
        ref={flameRef}
        className="fire-logo__flames"
        aria-hidden="true"
      />
      <canvas
        ref={emberRef}
        className="fire-logo__embers"
        aria-hidden="true"
      />
      <div className="fire-logo__heat" aria-hidden="true" />
      <img src={Logo} alt={alt} className="fire-logo__img" />
    </div>
  );
}
